import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { api } from "./api";
import { AuthProvider, useAuth } from "./auth";
import { AppShell, type NavigationItem } from "./components/AppShell";
import { CommandPalette, type Command } from "./components/CommandPalette";
import { KeyboardHelp } from "./components/KeyboardHelp";
import { SkeletonText } from "./components/Skeleton";
import { ToastProvider } from "./components/toast";
import { useHotkey } from "./keyboard";
import { AdminPage } from "./pages/admin/AdminPage";
import { LoginPage } from "./pages/LoginPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { ProjektPage } from "./pages/projekt/ProjektPage";
import { ProjektRumPage } from "./pages/projekt/ProjektRumPage";
import { SidorPage } from "./pages/sidor/SidorPage";
import { SokPage } from "./pages/sok/SokPage";
import { StartPage } from "./pages/StartPage";
import { QuickCaptureDialog } from "./pages/strommen/QuickCaptureDialog";
import { GenomgangPage } from "./pages/genomgang/GenomgangPage";
import { StrommenPage } from "./pages/strommen/StrommenPage";

const NAVIGATION: NavigationItem[] = [
  { to: "/", label: "Start" },
  { to: "/strommen", label: "Strömmen" },
  { to: "/genomgang", label: "Genomgång" },
  { to: "/sidor", label: "Sidor" },
  { to: "/projekt", label: "Projekt" },
  { to: "/sok", label: "Sök" },
  // The admin portal gathers every admin tool (WP0.11) — Designgrund lives inside it now.
  { to: "/admin", label: "Administration", adminOnly: true },
];

/** Routes behind this require a session; the rest of the app assumes a signed-in user. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { auth } = useAuth();

  if (auth.status === "loading") {
    return (
      <div className="mx-auto w-[min(440px,100%)] pt-[12vh]">
        <SkeletonText lines={3} />
      </div>
    );
  }
  if (auth.status === "signed_out" || auth.status === "unreachable") {
    return <Navigate to="/logga-in" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          <Route path="/logga-in" element={<LoginPage />} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <AuthedChrome />
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}

/** Everything inside the signed-in shell. Lives under RequireAuth so it can read the user's
 * role and filter admin surfaces out of navigation, palette and routes alike. */
function AuthedChrome() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const isAdmin = auth.status === "signed_in" && auth.user.role === "admin";

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Ctrl+K works even inside a text field — it is how you leave wherever you are.
  useHotkey("k", openPalette, { withModifier: true, whileTyping: true });
  useHotkey("?", () => setHelpOpen(true));
  // C for capture: the < 5 s path from thought to saved item, from anywhere.
  useHotkey("c", () => setCaptureOpen(true));

  const navigation = useMemo(
    () => NAVIGATION.filter((item) => !item.adminOnly || isAdmin),
    [isAdmin],
  );

  const commands = useMemo<Command[]>(
    () => [
      {
        id: "capture:new",
        label: "Snabbfångst — fånga en tanke",
        group: "Skapa",
        hint: "C",
        run: () => setCaptureOpen(true),
      },
      ...navigation.map((item) => ({
        id: `nav:${item.to}`,
        label: item.label,
        group: "Gå till",
        hint: item.pending,
        run: () => navigate(item.to),
      })),
      {
        id: "system:keyboard",
        label: "Visa tangentbordsgenvägar",
        group: "System",
        run: () => setHelpOpen(true),
      },
    ],
    [navigate, navigation],
  );

  // The palette is the primary navigation (docs/design.md) — it must reach pages too, not
  // only the static commands. Trigram-backed suggest keeps this under the 50 ms floor.
  const searchPages = useCallback(
    async (q: string): Promise<Command[]> => {
      const found = await api.suggest(q);
      return found
        .filter((s) => s.kind === "page")
        .map((s) => ({
          id: `page:${s.id}`,
          label: s.title,
          group: "Sidor",
          hint: s.hint ?? undefined,
          run: () => navigate(`/sidor/${s.id}`),
        }));
    },
    [navigate],
  );

  return (
    <>
      <AppShell navigation={navigation} onOpenPalette={openPalette}>
        <InnerRoutes isAdmin={isAdmin} />
      </AppShell>
      <CommandPalette
        open={paletteOpen}
        commands={commands}
        onSearch={searchPages}
        onClose={closePalette}
      />
      <KeyboardHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <QuickCaptureDialog open={captureOpen} onClose={() => setCaptureOpen(false)} />
    </>
  );
}

function InnerRoutes({ isAdmin }: { isAdmin: boolean }) {
  const notFound = (
    <PlaceholderPage
      title="Sidan finns inte"
      description="Adressen leder ingenstans. Öppna kommandopaletten med Ctrl + K för att hitta rätt."
      workPackage="—"
    />
  );

  return (
    <Routes>
      <Route path="/" element={<StartPage />} />
      {/* Same 404 as an unknown address for non-admins — the URL should not confirm that an
          admin surface exists (the pages API answers the same way for private spaces). */}
      <Route path="/admin" element={isAdmin ? <AdminPage /> : notFound} />
      {/* Designgrund moved into the portal; the old address keeps working for admins. */}
      <Route path="/design" element={isAdmin ? <Navigate to="/admin" replace /> : notFound} />
      <Route path="/strommen" element={<StrommenPage />} />
      <Route path="/genomgang" element={<GenomgangPage />} />
      <Route path="/sidor" element={<SidorPage />} />
      <Route path="/sidor/:pageId" element={<SidorPage />} />
      <Route path="/projekt" element={<ProjektPage />} />
      <Route path="/projekt/:projectId" element={<ProjektRumPage />} />
      <Route path="/sok" element={<SokPage />} />
      <Route path="*" element={notFound} />
    </Routes>
  );
}
